/**
 * TaskCreateActionCard — a proposed `ahoy_task` create, rendered as TaskCard.
 *
 * Every value edits in place and is written back to the PROPOSAL, not the CRM
 * (the task doesn't exist yet). The values don't all live in the same action:
 *
 *   - title, description and due date are PROPERTIES on this create action's
 *     own payload;
 *   - assignee and status are SIBLING `ahoy_association_added` actions that
 *     `actionMerging` folds into the card, so editing either rewrites THAT
 *     action's `target_entity_id`.
 *
 * A proposal that doesn't propose an association has no action to rewrite, and
 * the proposal-action editor can't create one — so that row stays read-only
 * rather than offering an editor that silently drops the change.
 */

import { useCallback, useMemo } from 'react';

import { useAssociationTypes } from '@/lib/crm/hooks/useAssociationTypes';
import { usePropertySchema } from '@/lib/crm/hooks/usePropertySchema';
import type { Note } from '@/lib/crm/record/memo/notes.types';
import {
  dueProperties,
  getMemoAssociationNames,
} from '@/lib/crm/record/memo/notes.utils';
import { useDefaultMemoStage } from '@/lib/crm/record/memo/useDefaultMemoStage';
import { TASK_ENTITY_TYPE } from '@/lib/crm/record/task/task.constants';
import { TaskCard } from '@/lib/crm/record/task/TaskCard';

import type {
  ActionPayload,
  AssociationAddedEntityPayload,
  ObjectCreatedEntityPayload,
  ProposedAction,
} from '../../lib/proposals/core/types';
import type { ActionRendererProps } from '../../lib/proposals/registry/types';
import type { MergedAssociation } from '../../lib/proposals/utils/actionMerging';
import { extractProperties, wrapProperties } from './CreateActionCard.helpers';
import { formatValue } from './formatValue';
import { useActionPayloadSave } from './shared';

// Stable empty-array identity so the default `associations` prop doesn't
// change reference each render.
const EMPTY_ASSOCIATIONS: MergedAssociation[] = [];

const TASK_ASSOCIATION_NAMES = getMemoAssociationNames(TASK_ENTITY_TYPE);

/** Both due properties are rewritten together — see `handleSaveDue`. */
const DUE_PROPERTY_KEYS = ['ahoy_date', 'ahoy_datetime'] as const;

/**
 * Placeholder action id bound into `useActionPayloadSave` when the matching
 * association isn't proposed. Hooks can't be called conditionally, and the hook
 * only builds a callback — no request is issued until it's invoked, and the
 * handlers that would invoke it are withheld in exactly that case.
 */
const NO_ACTION_ID = -1;

/**
 * The task as TaskCard reads it. It doesn't exist in the CRM yet, so the id
 * and timestamps are empty; stage and assignee come from the sibling
 * association actions, not from here.
 */
function synthesizeTask(
  action: ProposedAction<ObjectCreatedEntityPayload>
): Note {
  const properties = extractProperties(action.payload.payload.properties);
  return {
    entity_id: '',
    // Empty rather than a placeholder, so a placeholder can never be saved
    // verbatim as the real title.
    title: formatValue(properties['ahoy_title']) || '',
    content: formatValue(properties['ahoy_content']),
    created_at: '',
    updated_at: '',
    owner_entity_id: null,
    last_event_seq: 0,
    date: formatValue(properties['ahoy_date']) || undefined,
    datetime: formatValue(properties['ahoy_datetime']) || undefined,
    stage_entity_id: null,
    creator_entity_id: null,
    is_private: false,
  };
}

/** Rewrite an association action's target; '' means "no target". */
function withAssociationTarget(
  payload: ActionPayload,
  targetEntityId: string
): ActionPayload {
  const assocPayload = payload as AssociationAddedEntityPayload;
  return {
    ...assocPayload,
    payload: {
      ...assocPayload.payload,
      target_entity_id: targetEntityId,
    },
  };
}

interface TaskCreateActionCardProps {
  action: ProposedAction<ObjectCreatedEntityPayload>;
  proposalId?: number;
  orgId?: number;
  onUpdate?: ActionRendererProps['onUpdate'];
  /** Association siblings folded into this card (owner, stage, parent link…). */
  associations?: MergedAssociation[];
}

export function TaskCreateActionCard({
  action,
  proposalId,
  orgId,
  onUpdate,
  associations = EMPTY_ASSOCIATIONS,
}: TaskCreateActionCardProps) {
  // `orgId` is optional on this card; the hooks are bound to 0 in that case and
  // the card returns null below before anything renders.
  const { schema } = usePropertySchema(orgId ?? 0, TASK_ENTITY_TYPE);
  const { associationTypes } = useAssociationTypes(orgId ?? 0);
  // A task always shows a status. When none was proposed, show the org's
  // default stage — the one the record actually lands in on execution —
  // rather than an empty row that misrepresents what will happen.
  const defaultStageEntityId = useDefaultMemoStage(TASK_ENTITY_TYPE);

  const task = useMemo(() => synthesizeTask(action), [action]);
  const assigneeAssociationType = useMemo(
    () =>
      associationTypes.find(
        at => at.association_type_name === TASK_ASSOCIATION_NAMES.owner
      ),
    [associationTypes]
  );
  const ownerAssoc = useMemo(
    () =>
      associations.find(
        a => a.associationType === TASK_ASSOCIATION_NAMES.owner
      ),
    [associations]
  );
  const stageAssoc = useMemo(
    () =>
      associations.find(
        a => a.associationType === TASK_ASSOCIATION_NAMES.stage
      ),
    [associations]
  );

  const { savePayload } = useActionPayloadSave({
    orgId,
    proposalId,
    actionId: action.id,
    onUpdate,
  });
  const { savePayload: saveOwnerAssoc } = useActionPayloadSave({
    orgId,
    proposalId,
    actionId: ownerAssoc?.action.id ?? NO_ACTION_ID,
    onUpdate,
  });
  const { savePayload: saveStageAssoc } = useActionPayloadSave({
    orgId,
    proposalId,
    actionId: stageAssoc?.action.id ?? NO_ACTION_ID,
    onUpdate,
  });

  /** Rewrite the create action's properties: drop `removedKeys`, apply `changes`. */
  const saveCreateProperties = useCallback(
    async (
      changes: Record<string, unknown>,
      removedKeys: readonly string[] = []
    ) => {
      const current = Object.fromEntries(
        Object.entries(
          extractProperties(action.payload.payload.properties)
        ).filter(([key]) => !removedKeys.includes(key))
      );
      const updatedPayload: ObjectCreatedEntityPayload = {
        ...action.payload,
        payload: {
          ...action.payload.payload,
          properties: wrapProperties({ ...current, ...changes }),
        },
      };
      await savePayload(updatedPayload);
    },
    [action.payload, savePayload]
  );

  const handleSaveTitle = useCallback(
    async (title: string) => {
      await saveCreateProperties({ ahoy_title: title });
    },
    [saveCreateProperties]
  );

  const handleSaveDescription = useCallback(
    async (content: string) => {
      await saveCreateProperties({ ahoy_content: content });
    },
    [saveCreateProperties]
  );

  // The due value maps to the ahoy_date / ahoy_datetime pair (dueProperties).
  // Both are stripped first so a stale one can't re-persist, and in a create
  // payload "cleared" and "absent" are the same thing, so null keys are omitted.
  const handleSaveDue = useCallback(
    async (newDate: string | null) => {
      const due = dueProperties(newDate);
      await saveCreateProperties(
        {
          ...(due.ahoy_date != null && { ahoy_date: due.ahoy_date }),
          ...(due.ahoy_datetime != null && {
            ahoy_datetime: due.ahoy_datetime,
          }),
        },
        DUE_PROPERTY_KEYS
      );
    },
    [saveCreateProperties]
  );

  const handleSaveAssignee = useCallback(
    async (targetIds: string[]) => {
      const targetId = targetIds[0];
      // The owner is an `ahoy_association_added` action with no removal path in
      // the proposal editor. An empty target isn't a valid association target
      // (the backend rejects it and fails execution), so a clear is ignored
      // rather than written — the clear affordance is withheld too
      // (allowAssigneeClear={false}), so this only guards other entry points.
      if (!ownerAssoc || !targetId) {
        return;
      }
      await saveOwnerAssoc(
        withAssociationTarget(ownerAssoc.action.payload, targetId)
      );
    },
    [ownerAssoc, saveOwnerAssoc]
  );

  const handleStageChange = useCallback(
    async (_taskEntityId: string, newStageEntityId: string | null) => {
      if (!stageAssoc) {
        return;
      }
      await saveStageAssoc(
        withAssociationTarget(stageAssoc.action.payload, newStageEntityId ?? '')
      );
    },
    [stageAssoc, saveStageAssoc]
  );

  // Readonly contexts (no `onUpdate`) and missing proposal context can't save,
  // so every value stays read-only rather than accepting an edit that no-ops.
  const canEdit = Boolean(onUpdate && proposalId && orgId);

  if (!orgId) {
    return null;
  }

  return (
    <TaskCard
      task={task}
      orgId={orgId}
      // A cleared proposed stage/owner is an empty-string target; treat it as
      // absent so the default-stage fallback and the empty placeholder apply.
      stageEntityId={stageAssoc?.targetEntityId || defaultStageEntityId || null}
      ownerEntityId={ownerAssoc?.targetEntityId || null}
      dueDateSchema={schema['ahoy_date']}
      assigneeAssociationType={assigneeAssociationType}
      onSaveTitle={canEdit ? handleSaveTitle : undefined}
      onSaveDescription={canEdit ? handleSaveDescription : undefined}
      onSaveDue={canEdit ? handleSaveDue : undefined}
      onSaveAssignee={canEdit && ownerAssoc ? handleSaveAssignee : undefined}
      onStageChange={canEdit && stageAssoc ? handleStageChange : undefined}
      // The owner is an association-add action with no removal path in the
      // proposal editor; clearing would write an empty (invalid) target.
      allowAssigneeClear={false}
    />
  );
}
